import React, { forwardRef, useImperativeHandle, useRef, useReducer, useState, useContext } from "react";
import { ISLEntryGridItem } from "../../schemas/SLEntryGridSchemas";
import SLPageOverlay, { ISLPageOverlay } from "../SLPageOverlay";
import Section from "../../baseComponents/Section";
import { ActionTypes, IActionConfigurationsPerActionType, IActionConfiguration } from "./SLActionsManagerTypes";
import { CheckBoxSubmission, TextFieldSubmission, ICheckboxProps } from "./SubmissionComponents";
import SLEntriesDetails, { IProcessingStatus } from "../SLEntriesDetails";
import { Selection, Text } from "@fluentui/react";
import { ISLBatchPopupOverlay } from "../SLBatchPopup/SLBatchPopup";

import { AsyncSubject } from "rxjs";
import { IMessages, onProceedTextField, onProceedCheckbox } from "./SLActions";
import { INotificationMessagesRef } from "../NotificationMessages";
import LoadingSpinner from "../../baseComponents/LoadingSpinner";
import { getBatchEntryHeaders } from "../SLBatchPopup/SLBatchRequests";
import { UserContext, IUserProviderState } from "../../contexts/UserContext";
import { css } from "@emotion/css";
import { ISLBatchGridItem } from "../../schemas/SLBatchGridSchema";

export interface ISLActionsManager {
  props: {
    onNotifications: INotificationMessagesRef["setMessage"];
    batchPopup: ISLBatchPopupOverlay["ref"] | null;
  };
  ref: {
    open: (actionName: ActionTypes, items: ISLEntryGridItem[]) => void;
    getOnSubmitAsyncSubject: () => AsyncSubject<IMessages | null>;
  };
}

type dictionary = Record<string, string>;
const actionMapper: dictionary = {
  Post: "SubmitForReview",
  AddPosters: "AddPosters",
  ExportToExcel: "ExportToExcel",
  SendToPoster: "SendToPoster",
  ApproveEntries: "ApproveSLEntries",
  NeedClarification: "NeedsClarification",
  RecallEntries: "Recall",
  AddAttachments: "Attachments",
  Delete: "Delete"
};

enum ActionManagerLoadingStatus {
  Idle = "Idle",
  Pending = "Pending",
  Resolved = "Resolved",
  Rejected = "Rejected"
}

const ActionConfigurations: IActionConfigurationsPerActionType = {
  Post: {
    confirmText: "Post",
    labelText: "Approve SL Entry(-ies)",
    type: "checkBox",
    subText: "Please confirm action : Submit subledger entry(-ies) for Review."
  },
  AddPosters: {
    confirmText: "Add Posters",
    labelText: "Poster List",
    type: "textField",
    subText: "Please enter poster aliases separated by semi-colon ( ; )."
  },
  SendToPoster: {
    confirmText: "Send to Poster",
    labelText: "Poster List",
    type: "textField",
    subText: "Please enter poster aliases separated by semi-colon ( ; )."
  },
  NeedClarification: {
    confirmText: "Need Clarification",
    labelText: "Comments",
    type: "textField",
    subText: "Please provide comments for action."
  },
  ApproveEntries: {
    confirmText: "Approve Entries",
    labelText: "Approve SL Entry(-ies)",
    type: "checkBox",
    subText: "Please confirm action : Approve subledger entry(-ies)."
  },
  RecallEntries: {
    confirmText: "Recall Entries",
    labelText: "Recall SL Entry(-ies)",
    type: "checkBox",
    subText: "Please confirm action : Recall subledger entry(-ies)."
  },
  Delete: {
    confirmText: "Delete",
    labelText: "Confirm Delete",
    type: "checkBox",
    subText: "Please confirm action : Delete subledger entry(-ies)."
  },
  DeleteAttachments: {
    confirmText: "Delete Attachments",
    labelText: "Confirm Delete All Attachments",
    type: "checkBox",
    subText: "Please confirm action : Delete Attachments for subledger entry(-ies)."
  }
};

async function getEntriesFromBatches(
  batches: string[],
  actionName: string,
  getTokenFn: IUserProviderState["accessToken"]
) {
  if (actionName.includes("Post") || actionName.includes("Approve")) actionName = actionMapper[actionName];
  else actionName = "";
  const theBatchHeadersWithEntries = await getBatchEntryHeaders(batches, actionName, getTokenFn);
  const singularSLEntriesFromBatches = theBatchHeadersWithEntries.reduce((arr, batchHeader) => {
    const theEntries = batchHeader.slEntries;
    return arr.concat(theEntries);
  }, [] as ISLBatchGridItem[]);
  return singularSLEntriesFromBatches;
}

async function loadItemsOnActionManager(
  items: ISLEntryGridItem[],
  actionName: string,
  getTokenFn: IUserProviderState["accessToken"],
  dispatchLoadingStatus: React.Dispatch<React.SetStateAction<ActionManagerLoadingStatus>>,
  dispatchItems: React.Dispatch<React.SetStateAction<ISLEntryGridItem[]>>,
  dispatchMessage: React.Dispatch<React.SetStateAction<string>>,
  dispatchErrorMessage: React.Dispatch<React.SetStateAction<string>>
) {
  try {
    dispatchLoadingStatus(ActionManagerLoadingStatus.Pending);
    const batchRefGuidsToFetch = new Set<string>();
    const regularEntries = [] as ISLEntryGridItem[];
    for (const item of items) {
      if (item.batchRefguid && item.status !== "ValidationFailed") {
        batchRefGuidsToFetch.add(item.batchRefguid);
      } else {
        regularEntries.push(item);
      }
    }

    const batchGuids = Array.from(batchRefGuidsToFetch);
    const entriesFromBatches =
      batchGuids.length !== 0 ? await getEntriesFromBatches(batchGuids, actionName, getTokenFn) : [];
    const errorEntries: string[] = [];
    for (let i = 0; i < entriesFromBatches.length; i++) {
      if (!entriesFromBatches[i].isValidForAction) {
        const entryDetail: string = entriesFromBatches[i].nameDocumentId + "(" + entriesFromBatches[i].status + ")";
        errorEntries.push(entryDetail);
        entriesFromBatches.splice(i, 1);
        i--;
      }
    }
    const entries =
      entriesFromBatches.length !== 0
        ? ([] as ISLEntryGridItem[]).concat(regularEntries, entriesFromBatches)
        : regularEntries;

    dispatchLoadingStatus(ActionManagerLoadingStatus.Resolved);
    dispatchMessage(`Showing ${entries.length} Entries of ${items.length} Selected`);
    if (errorEntries.length > 0)
      dispatchErrorMessage(
        `Error : The action ${actionName} is not valid for some of the selected entry(-ies) or the entry(-ies) are awaiting posters action. Please correct the following entry(-ies) - 
        ${errorEntries.join(", ")}`
      );
    dispatchItems(entries);
  } catch (e) {
    dispatchLoadingStatus(ActionManagerLoadingStatus.Rejected);
    dispatchMessage("Could not load entries correctly.");
    dispatchItems([]);
  }
}

interface IActionManagerState {
  name: ActionTypes;
  configuration: IActionConfiguration;
  selection: Selection;
}

type settingsReducer = (
  state: null | IActionManagerState,
  newState: null | IActionManagerState
) => IActionManagerState | null;

let onSubmitAsyncSubject: null | AsyncSubject<IMessages | null> = null;

const SLActionsManager = forwardRef((props: ISLActionsManager["props"], ref: React.Ref<ISLActionsManager["ref"]>) => {
  const { accessToken } = useContext(UserContext);
  const pageOverlayRef = useRef<ISLPageOverlay["ref"]>(null);
  const [loadingStatus, setLoadingStatus] = useState<ActionManagerLoadingStatus>(ActionManagerLoadingStatus.Idle);
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const [processingStatus] = useState<IProcessingStatus["statuses"]>({});
  const [itemsForUI, setItemsForUi] = useState<ISLEntryGridItem[]>([]);
  const [userSelectedItems, setUserSelectedItems] = useState<ISLEntryGridItem[]>([]);
  const [message, setMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [settings, defineSettings] = useReducer<settingsReducer>((state, newState) => {
    return newState;
  }, null);

  useImperativeHandle(ref, (): ISLActionsManager["ref"] => ({
    open(name, selectedItems) {
      const configuration = ActionConfigurations[name];
      defineSettings({
        name,
        configuration,
        selection: new Selection()
      });
      setUserSelectedItems(selectedItems);
      loadItemsOnActionManager(
        selectedItems,
        name,
        accessToken,
        setLoadingStatus,
        setItemsForUi,
        setMessage,
        setErrorMessage
      );
      if (pageOverlayRef.current) {
        pageOverlayRef.current.open();
      }
    },
    getOnSubmitAsyncSubject() {
      onSubmitAsyncSubject = new AsyncSubject<IMessages | null>();
      return onSubmitAsyncSubject;
    }
  }));

  const closeOverlay = () => {
    defineSettings(null);
    setMessage("");
    setErrorMessage("");
    setItemsForUi([]);
    setUserSelectedItems([]);
    if (pageOverlayRef.current) {
      pageOverlayRef.current.close();
    }
  };

  const onSubmit: ICheckboxProps["onSubmit"] = async (type, text?: string) => {
    if (type === "cancel") {
      if (onSubmitAsyncSubject) {
        onSubmitAsyncSubject.next(null);
        onSubmitAsyncSubject.complete();
        onSubmitAsyncSubject = null;
      }
      closeOverlay();
    } else {
      if (errorMessage.length <= 0) {
        if (settings) {
          const action = settings.name;
          const result = await (action === "AddPosters" || action === "SendToPoster" || action === "NeedClarification"
            ? onProceedTextField(accessToken, action, userSelectedItems, text || "")
            : onProceedCheckbox(accessToken, action, userSelectedItems));
          if (onSubmitAsyncSubject) {
            onSubmitAsyncSubject.next(result);
            onSubmitAsyncSubject.complete();
            onSubmitAsyncSubject = null;
          }
          closeOverlay();
        }
      } else {
        alert("Can't perform this action at this moment. Please check the error message.");
      }
    }
  };

  const onBatchPopupOpened = () => {
    if (props.batchPopup) {
      const onBatchPopupCloseEvent = props.batchPopup.getOnCloseAsyncSubject();
      onBatchPopupCloseEvent.subscribe(() => {
        if (pageOverlayRef.current) {
          pageOverlayRef.current.open();
        }
      });
    }
    if (pageOverlayRef.current) {
      pageOverlayRef.current.close();
    }
  };

  return (
    <React.Fragment>
      <SLPageOverlay
        ref={pageOverlayRef}
        label="Summary of Entries"
        onClose={() => {
          if (onSubmitAsyncSubject) {
            onSubmitAsyncSubject.next(null);
            onSubmitAsyncSubject.complete();
            onSubmitAsyncSubject = null;
          }
          closeOverlay();
        }}
      >
        {settings === null ? null : (
          <React.Fragment>
            <div
              className={css`
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
              `}
            >
              <div className="label-container">
                <Text
                  nowrap
                  variant="medium"
                  block
                  style={{
                    marginTop: "4px",
                    color: "var(--accent-font-color, #727272)"
                  }}
                >
                  {message}
                </Text>
              </div>
            </div>
            <div>
              <div className="label-container">
                <Text
                  nowrap
                  variant="medium"
                  block
                  style={{
                    marginTop: "4px",
                    color: "var(--error-font-color, red)",
                    whiteSpace: "pre-line"
                  }}
                >
                  {errorMessage}
                </Text>
              </div>
            </div>
            <Section label="SL Entries">
              {loadingStatus === ActionManagerLoadingStatus.Pending ? (
                <LoadingSpinner label="Loading Details" />
              ) : loadingStatus === ActionManagerLoadingStatus.Rejected ? (
                <Text variant="xLarge">Unable to load details for entries</Text>
              ) : itemsForUI.length === 0 ? (
                <Text
                  variant="xLarge"
                  style={{
                    color: "var(--accent-font-color, #727272)",
                    textAlign: "center"
                  }}
                >
                  No items to show.
                </Text>
              ) : (
                <SLEntriesDetails
                  items={itemsForUI}
                  selection={settings.selection}
                  processingStatus={processingStatus}
                  batchPopup={props.batchPopup}
                  onPopupOpened={onBatchPopupOpened}
                  newTab={true}
                  routeState={() => undefined}
                />
              )}
            </Section>
            {settings.configuration.type === "checkBox" ? (
              <Section label={settings.configuration.labelText}>
                <CheckBoxSubmission {...settings.configuration} onSubmit={onSubmit} />
              </Section>
            ) : (
              <Section label={settings.configuration.labelText}>
                <TextFieldSubmission {...settings.configuration} onSubmit={onSubmit} />
              </Section>
            )}
          </React.Fragment>
        )}
      </SLPageOverlay>
    </React.Fragment>
  );
});

export default SLActionsManager;
